import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { toast, type ToastOptions } from '@/components'

import { FormReferenceHandle, FormStatus, FormState } from './Form'

export type FormToastMode = 'both' | 'error' | 'success' | 'none'

export const useForm = ({
    toastMode = 'error',
    formRef: sourceFormRef,
    toastOptions = {},
}: {
    toastMode?: FormToastMode
    formRef?: RefObject<FormReferenceHandle>
    toastOptions?: ToastOptions
} = {}) => {
    const localFormRef = useRef<FormReferenceHandle>(null)
    const formRef = sourceFormRef || localFormRef
    const [formState, setFormState] = useState<FormState>({
        status: 'idle',
        result: null,
    })

    const previousFormStatusRef = useRef<FormStatus>('idle')

    const { t } = useTranslation()

    useEffect(() => {
        const { status: formStatus, result: formResult } = formState

        // if form status didn't changed we do nothing
        if (previousFormStatusRef.current === formStatus) {
            return
        }

        // update the previous form status
        previousFormStatusRef.current = formStatus

        if (toastMode) {
            if (formStatus === 'error' && (toastMode === 'error' || toastMode === 'both')) {
                toast.error(
                    typeof formResult?.error === 'string' ? formResult.error : t('components.form.submitError'),
                    toastOptions
                )
            } else if (formStatus === 'submitted' && (toastMode === 'success' || toastMode === 'both')) {
                if (typeof formResult?.successMessage === 'string') {
                    if (formResult.successMessage) {
                        toast.success(formResult.successMessage, { duration: formResult.duration, ...toastOptions })
                    }
                } else {
                    toast.success(t('components.form.submitSuccess'), toastOptions)
                }
            }
        }
    }, [formState, toastMode, t, toastOptions])

    const { status: formStatus, result: formResult } = formState
    const isSubmittingForm = formStatus === 'submitting'
    const resetForm = useCallback(() => {
        setFormState({
            status: 'idle',
            result: null,
        })
    }, [])

    return {
        formRef,
        formStatus,
        formResult,
        isSubmittingForm,
        resetForm,
        formProps: {
            ref: formRef,
            status: formStatus,
            result: formResult,
            onState: setFormState,
        },
    }
}
